// frontend/src/pages/admin/AdminUsers.js
import React, { useState, useEffect, useMemo } from 'react';
import { usersApi } from '../../api/users';
import { zonesApi } from '../../api/zones'; 
import { walletApi } from '../../api/wallet'; 
import { apiClient } from '../../api'; 
import helpers from '../../utils/helpers'; 
import { useAuth } from '../../context/AuthContext'; 
import { 
    FaSearch, FaEdit, FaTimes, FaCheck, 
    FaCrown, FaSpinner, FaUserPlus, FaHistory, FaBan, FaUnlock, FaKey, 
    FaBox, FaTools, FaTicketAlt, FaTrash, FaDownload, FaEnvelope, FaMapMarkedAlt, 
    FaGlobe, FaLock, FaListAlt, FaMoneyBillWave, FaExclamationTriangle 
} from 'react-icons/fa';

// --- CONFIGURATION ---
const MASTER_ADMIN_EMAILS = ["esekiegabriel@gmail.com", "thomasesekie@gmail.com"];

// --- CREDIT REASONS DROPDOWN ---
const CREDIT_REASONS = [
    "Vehicle Listing",
    "Vehicle Image Listing",
    "AI Services",
    "Boost / Promotion",
    "Car Inspection",
    "Snap & Save",
    "Others"
];

// --- PERMISSION CONSTANTS ---
const ALL_TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'orders', label: 'Orders Management' },
    { id: 'users', label: 'User Management' },
    { id: 'services', label: 'Service Bookings' },
    { id: 'providers', label: 'Service Network' },
    { id: 'inventory', label: 'Parts Inventory' },
    { id: 'vehicles', label: 'Vehicle Marketplace' },
    { id: 'finance', label: 'Financials' },
    { id: 'payouts', label: 'Payout Requests' },
    { id: 'delivery', label: 'Logistics & Delivery' },
    { id: 'support', label: 'Support Desk' },
    { id: 'reviews', label: 'Review Moderation' },
    { id: 'reports', label: 'Inventory Reports' },
    { id: 'bulk', label: 'Bulk Requests' },
    { id: 'ea_hub', label: 'EA Hub (Exclusive)' },
    { id: 'ai_analyst', label: 'AI Executive Analyst' },
    { id: 'retention', label: 'Retention Engine' },
    { id: 'analytics', label: 'Deep Analytics' },
    { id: 'shipping', label: 'Shipping Rates' },
    { id: 'ai_logs', label: 'AI Chat Logs' },
    { id: 'catalog', label: 'Catalog Management' },
    { id: 'ads', label: 'Ads Center' },
    { id: 'zones', label: 'Zone Management' },
    { id: 'stores', label: 'Store Management' },
    { id: 'content', label: 'Content CMS' },
    { id: 'settings', label: 'System Settings' },
    { id: 'ai_system', label: 'AI System Settings' },
    { id: 'reset', label: 'Reset to Default' }
];

const USER_ACTIONS = [
    { id: 'view_history', label: 'View User History' },
    { id: 'manage_roles', label: 'Manage Roles' },
    { id: 'reset_password', label: 'Reset Password' },
    { id: 'ban_user', label: 'Ban/Unban User' },
    { id: 'manual_credit', label: 'Manual Wallet Credit' } 
];

const SYSTEM_ROLES = [
    'admin', 
    'store_manager', 
    'dealer', 
    'technician', 
    'logistics_manager', 
    'territory_coordinator'
];

function AdminUsers() {
  const { user: currentUser } = useAuth(); 
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- LOCATION DATA ---
  const [lgas, setLgas] = useState([]);
  const [zones, setZones] = useState([]);
  
  // --- BULK SELECTION ---
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // --- MODAL STATES ---
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeModalTab, setActiveModalTab] = useState('roles'); 
  const [roleSelection, setRoleSelection] = useState([]);
  
  // --- TERRITORY STATE ---
  const [coordinatorLevel, setCoordinatorLevel] = useState('state');
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedLgas, setSelectedLgas] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);

  // --- MICRO-PERMISSIONS STATE ---
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [restrictedActions, setRestrictedActions] = useState([]);
  
  const [saving, setSaving] = useState(false);
  
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [registerData, setRegisterData] = useState({ full_name: '', email: '', password: '' });
  
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyData, setHistoryData] = useState({ orders: [], bookings: [], tickets: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState('orders');

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // --- MANUAL CREDIT STATE ---
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditUser, setCreditUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState(CREDIT_REASONS[0]); // Default
  const [customReason, setCustomReason] = useState(''); // For "Others"

  useEffect(() => {
    fetchUsers();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      setFilteredUsers(users.filter(u => 
        (u.email && u.email.toLowerCase().includes(lower)) ||
        (u.full_name && u.full_name.toLowerCase().includes(lower))
      ));
    } else {
      setFilteredUsers(users);
    }
    setSelectedIds([]);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const data = await usersApi.getAllUsersAdmin();
      setUsers(data);
      setFilteredUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      alert("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
      try {
          const [lgasData, zonesData] = await Promise.all([
              zonesApi.getAllLgas(),
              zonesApi.getAllZones()
          ]);
          setLgas(lgasData);
          setZones(zonesData);
      } catch (e) {
          console.error("Failed to load locations", e);
      }
  };

  const lgasByState = useMemo(() => {
    return lgas.reduce((acc, lga) => {
      if (!lga.state || !lga.lga) return acc;
      const stateKey = lga.state.trim();
      if (!acc[stateKey]) acc[stateKey] = [];
      acc[stateKey].push(lga);
      return acc;
    }, {});
  }, [lgas]);

  const zonesByLgaId = useMemo(() => {
      return zones.reduce((acc, zone) => {
          if (!zone.lga_id) return acc;
          if (!acc[zone.lga_id]) acc[zone.lga_id] = [];
          acc[zone.lga_id].push(zone);
          return acc;
      }, {});
  }, [zones]);

  // --- BULK HANDLERS ---
  const handleSelectAll = (e) => {
      if (e.target.checked) {
          setSelectedIds(filteredUsers.map(u => u.id));
      } else {
          setSelectedIds([]);
      }
  };

  const handleSelectOne = (id) => {
      setSelectedIds(prev => 
          prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
  };

  const handleBulkDelete = async () => {
      if (selectedIds.length === 0) return;
      if (!window.confirm(`⚠️ NUCLEAR OPTION: Permanently delete ${selectedIds.length} users?`)) return;

      setIsBulkProcessing(true);
      try {
          const res = await apiClient.delete('/users/admin/bulk-delete', { body: { user_ids: selectedIds } });
          alert(res?.message || "Users deleted successfully.");
          setSelectedIds([]);
          fetchUsers();
      } catch (err) {
          alert(err?.message || "Failed to delete users.");
          fetchUsers(); // refresh anyway, so the list always matches reality
      } finally {
          setIsBulkProcessing(false);
      }
  };

  const handleBulkExport = async (format) => {
      if (selectedIds.length === 0 && !window.confirm("Export ALL users?")) return;
      setIsBulkProcessing(true);
      try {
          const response = await apiClient.post('/users/admin/bulk-export', { user_ids: selectedIds, format });
          const blob = new Blob([format === 'json' ? JSON.stringify(response) : response], { type: format === 'json' ? 'application/json' : 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `users_export.${format}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
      } catch (err) { alert("Export failed."); } 
      finally { setIsBulkProcessing(false); }
  };

  const handleBulkPasswordReset = async () => {
      if (selectedIds.length === 0) return;
      if (!window.confirm(`Send password reset emails to ${selectedIds.length} users?`)) return;
      setIsBulkProcessing(true);
      try {
          await apiClient.post('/users/admin/bulk-reset-password', { user_ids: selectedIds });
          alert("Reset emails sent.");
          setSelectedIds([]);
      } catch (err) { alert("Failed to send resets."); } 
      finally { setIsBulkProcessing(false); }
  };

  // --- ROLE & PERMISSION HANDLERS ---
  const openRoleModal = (user) => {
    setSelectedUser(user);
    setActiveModalTab('roles'); 
    
    if (MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
        setRoleSelection(['admin', 'super_admin', ...user.roles]);
    } else {
        setRoleSelection(user.roles || []);
    }
    
    setSelectedStates(user.managed_states || []);
    setSelectedLgas(user.managed_lgas || []);
    setSelectedZones(user.managed_zones || []);
    if (user.managed_zones?.length > 0) setCoordinatorLevel('zone');
    else if (user.managed_lgas?.length > 0) setCoordinatorLevel('lga');
    else setCoordinatorLevel('state');

    setAllowedTabs(user.permissions?.allowed_tabs || []);
    setRestrictedActions(user.permissions?.restricted_actions || []);
  };

  const toggleRole = (role) => {
    if (selectedUser && MASTER_ADMIN_EMAILS.includes(selectedUser.email.toLowerCase()) && role === 'admin') return;
    if (roleSelection.includes(role)) {
      setRoleSelection(prev => prev.filter(r => r !== role));
    } else {
      setRoleSelection(prev => [...prev, role]);
    }
  };

  const toggleTabPermission = (tabId) => {
      setAllowedTabs(prev => prev.includes(tabId) ? prev.filter(t => t !== tabId) : [...prev, tabId]);
  };

  const toggleActionRestriction = (actionId) => {
      setRestrictedActions(prev => prev.includes(actionId) ? prev.filter(a => a !== actionId) : [...prev, actionId]);
  };

  const toggleState = (state) => {
      setSelectedStates(prev => {
          const newStates = prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state];
          if (!newStates.includes(state)) {
              const lgasToRemove = lgasByState[state]?.map(l => l.lga) || [];
              const lgaIdsToRemove = lgasByState[state]?.map(l => l.id) || [];
              setSelectedLgas(curr => curr.filter(l => !lgasToRemove.includes(l)));
              const zonesToRemove = [];
              lgaIdsToRemove.forEach(lgaId => {
                  const zList = zonesByLgaId[lgaId] || [];
                  zList.forEach(z => zonesToRemove.push(z.zone_name));
              });
              setSelectedZones(curr => curr.filter(z => !zonesToRemove.includes(z)));
          }
          return newStates;
      });
  };
  
  const toggleLga = (lgaName, lgaId) => {
      setSelectedLgas(prev => {
          const newLgas = prev.includes(lgaName) ? prev.filter(l => l !== lgaName) : [...prev, lgaName];
          if (!newLgas.includes(lgaName)) {
              const zonesToRemove = zonesByLgaId[lgaId]?.map(z => z.zone_name) || [];
              setSelectedZones(curr => curr.filter(z => !zonesToRemove.includes(z)));
          }
          return newLgas;
      });
  };
  
  const toggleZone = (zoneName) => {
      setSelectedZones(prev => prev.includes(zoneName) ? prev.filter(z => z !== zoneName) : [...prev, zoneName]);
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;
    
    if (roleSelection.includes('territory_coordinator') && 
        (selectedStates.length === 0 && selectedLgas.length === 0 && selectedZones.length === 0)) {
        alert("Please assign at least one territory.");
        return;
    }

    setSaving(true);
    try {
      const permissionsPayload = {
          allowed_tabs: allowedTabs,
          restricted_actions: restrictedActions
      };

      await usersApi.updateUserRoles(
          selectedUser.id, 
          roleSelection, 
          selectedStates, 
          selectedLgas, 
          selectedZones,
          permissionsPayload
      );
      
      const updatedUsers = users.map(u => u.id === selectedUser.id ? { 
          ...u, 
          roles: roleSelection,
          managed_states: selectedStates,
          managed_lgas: selectedLgas,
          managed_zones: selectedZones,
          permissions: permissionsPayload
      } : u);
      
      setUsers(updatedUsers);
      alert(`Profile updated for ${selectedUser.email}`);
      setSelectedUser(null);
    } catch (err) {
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const canPerformAction = (actionId) => {
      if (currentUser?.email && MASTER_ADMIN_EMAILS.includes(currentUser.email.toLowerCase())) return true;
      if (currentUser?.permissions?.restricted_actions?.includes(actionId)) {
          return false;
      }
      return true;
  };

  const handleViewHistory = async (user) => {
      if (!canPerformAction('view_history')) return alert("Access Denied: You are restricted from viewing history.");
      setHistoryUser(user);
      setHistoryModalOpen(true);
      setLoadingHistory(true);
      try {
          const data = await usersApi.getUserHistory(user.id);
          setHistoryData(data);
      } catch (err) {
          alert("Failed to load history.");
      } finally {
          setLoadingHistory(false);
      }
  };

  const handleToggleBan = async (user) => {
      if (!canPerformAction('ban_user')) return alert("Access Denied: You are restricted from banning users.");
      const action = user.is_banned ? 'unban' : 'ban';
      if (!window.confirm(`${action.toUpperCase()} ${user.email}?`)) return;
      try {
          await usersApi.toggleUserBan(user.id, action);
          setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_banned: !user.is_banned } : u));
      } catch (err) {
          alert(`Failed to ${action} user.`);
      }
  };

  const openPasswordModal = (user) => {
      if (!canPerformAction('reset_password')) return alert("Access Denied: You are restricted from resetting passwords.");
      setPasswordUser(user);
      setNewPassword('');
      setPasswordModalOpen(true);
  };

  const handlePasswordReset = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
          await usersApi.resetUserPassword(passwordUser.id, newPassword);
          alert(`Password reset.`);
          setPasswordModalOpen(false);
      } catch (err) {
          alert("Failed to reset.");
      } finally {
          setSaving(false);
      }
  };
  
  const handleRegisterCustomer = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
          await usersApi.registerCustomerAdmin(registerData);
          alert("Customer registered.");
          setRegisterData({ full_name: '', email: '', password: '' });
          setIsRegisterModalOpen(false);
          fetchUsers();
      } catch (err) {
          alert(err.data?.message || "Registration failed.");
      } finally {
          setSaving(false);
      }
  };

  // --- MANUAL CREDIT HANDLERS ---
  const openCreditModal = (user) => {
      if (!canPerformAction('manual_credit')) return alert("Access Denied: You are restricted from crediting wallets.");
      setCreditUser(user);
      setCreditAmount('');
      setCreditReason(CREDIT_REASONS[0]);
      setCustomReason('');
      setCreditModalOpen(true);
  };

  const handleManualCredit = async (e) => {
      e.preventDefault();
      if (!creditAmount || parseFloat(creditAmount) <= 0) return alert("Enter a valid amount.");
      
      let finalReason = creditReason;
      if (creditReason === 'Others') {
          if (!customReason.trim()) return alert("Please specify the reason.");
          finalReason = customReason;
      }

      if (!window.confirm(`Credit NGN ${helpers.formatCurrency(creditAmount, 'NGN')} to ${creditUser.full_name}? This action is logged.`)) return;

      setSaving(true);
      try {
          await walletApi.manualCredit({
              user_id: creditUser.id,
              amount: parseFloat(creditAmount),
              reason: finalReason
          });
          alert("Wallet credited successfully!");
          setCreditModalOpen(false);
      } catch (err) {
          alert(err.data?.message || "Failed to credit wallet.");
      } finally {
          setSaving(false);
      }
  };

  const getRoleBadge = (user) => {
    // --- UPDATED: Check against list of Master Admins ---
    if (MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
        return <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1"><FaCrown /> Master</span>;
    }
    if (!user.roles || user.roles.length === 0) return <span className="text-xs text-gray-400">Customer</span>;
    return user.roles.map(role => {
        let label = role.replace('_', ' ');
        if (role === 'dealer') label = 'Car Dealer';
        return (
            <span key={role} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full mr-1 capitalize">{label}</span>
        );
    });
  };

  if (loading) return <div className="p-8 text-center">Loading users...</div>;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <h1 className="text-2xl font-bold text-gray-800 flex-shrink-0">User Management</h1>
        <button onClick={() => setIsRegisterModalOpen(true)} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2 flex-shrink-0"><FaUserPlus /> Register Customer</button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="relative max-w-md">
          <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-primary" />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* BULK ACTIONS */}
      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4 flex flex-wrap items-center justify-between gap-4 animate-fadeIn">
          <div className="flex items-center gap-3">
              <input type="checkbox" onChange={handleSelectAll} checked={filteredUsers.length > 0 && selectedIds.length === filteredUsers.length} className="h-5 w-5 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer" />
              <span className="font-bold text-blue-800 text-sm">{selectedIds.length > 0 ? `${selectedIds.length} Selected` : "Select All"}</span>
          </div>
          <div className="flex gap-2">
              <button onClick={() => handleBulkExport('csv')} disabled={isBulkProcessing} className="bg-white border border-gray-300 text-gray-700 px-3 py-1 rounded text-xs font-bold hover:bg-gray-50 flex items-center gap-1"><FaDownload /> Export CSV</button>
              {selectedIds.length > 0 && (
                  <>
                      <button onClick={handleBulkPasswordReset} disabled={isBulkProcessing} className="bg-yellow-500 text-white px-3 py-1 rounded text-xs font-bold hover:bg-yellow-600 flex items-center gap-1"><FaEnvelope /> Force Reset</button>
                      <button onClick={handleBulkDelete} disabled={isBulkProcessing} className="bg-red-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-red-700 flex items-center gap-1">{isBulkProcessing ? <FaSpinner className="animate-spin" /> : <FaTrash />} Delete</button>
                  </>
              )}
          </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                <th className="p-4 w-10"></th>
                <th className="p-4">User</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Roles</th>
                <th className="p-4">Territory</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.map((user) => (
                <tr key={user.id} className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(user.id) ? 'bg-blue-50' : ''}`}>
                  <td className="p-4"><input type="checkbox" checked={selectedIds.includes(user.id)} onChange={() => handleSelectOne(user.id)} className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer" /></td>
                  <td className="p-4"><div className="font-bold text-gray-800">{user.full_name || 'No Name'}</div><div className="text-xs text-gray-500">Joined: {new Date(user.created_at).toLocaleDateString()}</div></td>
                  <td className="p-4 text-sm"><div className="text-gray-800">{user.email}</div><div className="text-gray-500 text-xs">{user.phone || 'No Phone'}</div></td>
                  <td className="p-4"><div className="flex flex-wrap gap-1">{getRoleBadge(user)}</div></td>
                  <td className="p-4 text-sm text-gray-600">
                      {user.managed_states?.length > 0 && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-bold mr-1">{user.managed_states.length} States</span>}
                      {user.managed_lgas?.length > 0 && <span className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs font-bold mr-1">{user.managed_lgas.length} LGAs</span>}
                      {user.managed_zones?.length > 0 && <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs font-bold">{user.managed_zones.length} Zones</span>}
                      {(!user.managed_states?.length && !user.managed_lgas?.length && !user.managed_zones?.length) && '-'}
                  </td>
                  <td className="p-4">{user.is_banned ? <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold">BANNED</span> : <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-bold">Active</span>}</td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                        {/* MANUAL CREDIT BUTTON */}
                        {canPerformAction('manual_credit') && (
                            <button 
                                onClick={() => openCreditModal(user)} 
                                className="bg-green-50 text-green-600 p-2 rounded hover:bg-green-100" 
                                title="Credit Wallet"
                            >
                                <FaMoneyBillWave />
                            </button>
                        )}

                        {canPerformAction('view_history') && <button onClick={() => handleViewHistory(user)} className="bg-blue-50 text-blue-600 p-2 rounded hover:bg-blue-100" title="View History"><FaHistory /></button>}
                        {canPerformAction('manage_roles') && <button onClick={() => openRoleModal(user)} className="bg-gray-100 text-gray-600 p-2 rounded hover:bg-gray-200" title="Manage Roles"><FaEdit /></button>}
                        {canPerformAction('reset_password') && <button onClick={() => openPasswordModal(user)} className="bg-yellow-50 text-yellow-600 p-2 rounded hover:bg-yellow-100" title="Reset Password"><FaKey /></button>}
                        
                        {/* --- BAN BUTTON (HIDDEN FOR MASTER ADMINS) --- */}
                        {!MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase()) && canPerformAction('ban_user') && (
                            <button 
                                onClick={() => handleToggleBan(user)} 
                                className={`p-2 rounded ${user.is_banned ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-red-100 text-red-600 hover:bg-red-200'}`} 
                                title={user.is_banned ? "Unban User" : "Ban User"}
                            >
                                {user.is_banned ? <FaUnlock /> : <FaBan />}
                            </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- MANUAL CREDIT MODAL (UPDATED) --- */}
      {creditModalOpen && creditUser && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 animate-fadeInUp">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                      <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                          <FaMoneyBillWave className="text-green-600" /> Credit Wallet
                      </h3>
                      <button onClick={() => setCreditModalOpen(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded text-sm text-blue-800 mb-4 flex items-start gap-2">
                      <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                      <p>
                          You are manually adding funds to <strong>{creditUser.full_name}</strong>. 
                          This action is logged in the <strong>Financial Audit Trail</strong>.
                      </p>
                  </div>

                  <form onSubmit={handleManualCredit} className="space-y-4">
                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Amount (NGN)</label>
                          <input 
                              type="number" 
                              value={creditAmount} 
                              onChange={e => setCreditAmount(e.target.value)} 
                              className="w-full p-3 border rounded focus:ring-2 focus:ring-green-500 outline-none font-bold text-lg" 
                              placeholder="0.00" 
                              required 
                              min="1"
                          />
                      </div>
                      
                      {/* --- REASON DROPDOWN --- */}
                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Reason / Purpose</label>
                          <select 
                              value={creditReason} 
                              onChange={e => setCreditReason(e.target.value)} 
                              className="w-full p-3 border rounded focus:ring-2 focus:ring-green-500 outline-none bg-white"
                          >
                              {CREDIT_REASONS.map(reason => (
                                  <option key={reason} value={reason}>{reason}</option>
                              ))}
                          </select>
                      </div>

                      {/* --- CUSTOM REASON INPUT --- */}
                      {creditReason === 'Others' && (
                          <div className="animate-fadeIn">
                              <label className="block text-xs font-bold text-gray-600 mb-1">Specify Reason</label>
                              <textarea 
                                  value={customReason} 
                                  onChange={e => setCustomReason(e.target.value)} 
                                  className="w-full p-3 border rounded focus:ring-2 focus:ring-green-500 outline-none" 
                                  rows="2" 
                                  placeholder="Enter specific reason..." 
                                  required 
                              ></textarea>
                          </div>
                      )}
                      
                      <div className="flex justify-end gap-2 pt-2">
                          <button type="button" onClick={() => setCreditModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                          <button 
                              type="submit" 
                              disabled={saving} 
                              className="bg-green-600 text-white px-6 py-2 rounded font-bold hover:bg-green-700 flex items-center gap-2 shadow-sm"
                          >
                              {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />} Confirm Credit
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* --- HISTORY MODAL --- */}
      {historyModalOpen && historyUser && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fadeInUp">
                  <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                      <div><h3 className="text-xl font-bold text-gray-800">User History</h3><p className="text-sm text-gray-500">{historyUser.full_name} ({historyUser.email})</p></div>
                      <button onClick={() => setHistoryModalOpen(false)}><FaTimes size={20} /></button>
                  </div>
                  <div className="flex border-b border-gray-200">
                      <button onClick={() => setActiveHistoryTab('orders')} className={`flex-1 py-3 font-bold text-sm ${activeHistoryTab === 'orders' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>Orders ({historyData.orders.length})</button>
                      <button onClick={() => setActiveHistoryTab('bookings')} className={`flex-1 py-3 font-bold text-sm ${activeHistoryTab === 'bookings' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>Bookings ({historyData.bookings.length})</button>
                      <button onClick={() => setActiveHistoryTab('tickets')} className={`flex-1 py-3 font-bold text-sm ${activeHistoryTab === 'tickets' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>Tickets ({historyData.tickets.length})</button>
                  </div>
                  <div className="p-6 overflow-y-auto bg-gray-50 flex-grow">
                      {loadingHistory ? <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading...</div> : (
                          <>
                              {activeHistoryTab === 'orders' && (
                                  <div className="space-y-3">
                                      {historyData.orders.length === 0 ? <p className="text-center text-gray-500">No orders found.</p> : historyData.orders.map(o => (
                                          <div key={o.id} className="bg-white p-4 rounded border flex justify-between items-center">
                                              <div><p className="font-bold text-primary flex items-center gap-2"><FaBox /> {o.order_number}</p><p className="text-xs text-gray-500">{new Date(o.created_at).toLocaleDateString()}</p></div>
                                              <div className="text-right"><p className="font-bold">{helpers.formatCurrency(o.total_amount, 'NGN')}</p><span className="text-xs bg-gray-100 px-2 py-1 rounded uppercase font-bold">{o.status}</span></div>
                                          </div>
                                      ))}
                                  </div>
                              )}
                              {activeHistoryTab === 'bookings' && (
                                  <div className="space-y-3">
                                      {historyData.bookings.length === 0 ? <p className="text-center text-gray-500">No bookings found.</p> : historyData.bookings.map(b => (
                                          <div key={b.id} className="bg-white p-4 rounded border flex justify-between items-center">
                                              <div><p className="font-bold text-purple-600 flex items-center gap-2"><FaTools /> {b.service_type.replace('_', ' ')}</p><p className="text-xs text-gray-500">{b.vehicle_info_snapshot}</p></div>
                                              <div className="text-right"><p className="text-sm">{b.preferred_date}</p><span className="text-xs bg-gray-100 px-2 py-1 rounded uppercase font-bold">{b.status}</span></div>
                                          </div>
                                      ))}
                                  </div>
                              )}
                              {activeHistoryTab === 'tickets' && (
                                  <div className="space-y-3">
                                      {historyData.tickets.length === 0 ? <p className="text-center text-gray-500">No tickets found.</p> : historyData.tickets.map(t => (
                                          <div key={t.id} className="bg-white p-4 rounded border flex justify-between items-center">
                                              <div><p className="font-bold text-blue-600 flex items-center gap-2"><FaTicketAlt /> {t.ticket_id}</p><p className="text-sm text-gray-700">{t.subject}</p></div>
                                              <div className="text-right"><span className="text-xs bg-gray-100 px-2 py-1 rounded uppercase font-bold">{t.status}</span></div>
                                          </div>
                                      ))}
                                  </div>
                              )}
                          </>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* --- PASSWORD RESET MODAL --- */}
      {passwordModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fadeInUp">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Reset Password</h3>
                  <p className="text-sm text-gray-600 mb-4">Set a new temporary password for <strong>{passwordUser.email}</strong>.</p>
                  <form onSubmit={handlePasswordReset}>
                      <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full p-3 border rounded mb-4" placeholder="Enter new password (min 6 chars)" required minLength={6} />
                      <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => setPasswordModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button type="submit" disabled={saving} className="bg-yellow-500 text-white px-4 py-2 rounded font-bold hover:bg-yellow-600">{saving ? 'Resetting...' : 'Reset Password'}</button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* --- ROLE MODAL (TABBED INTERFACE) --- */}
      {selectedUser && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl animate-fadeInUp max-h-[90vh] flex flex-col">
            
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-800">Manage User: {selectedUser.full_name}</h3>
              <button onClick={() => setSelectedUser(null)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50">
                <button 
                    onClick={() => setActiveModalTab('roles')}
                    className={`flex-1 py-4 text-sm font-bold flex items-center justify-center gap-2 ${activeModalTab === 'roles' ? 'bg-white border-t-2 border-primary text-primary' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                    <FaGlobe /> Roles & Territory
                </button>
                <button 
                    onClick={() => setActiveModalTab('permissions')}
                    className={`flex-1 py-4 text-sm font-bold flex items-center justify-center gap-2 ${activeModalTab === 'permissions' ? 'bg-white border-t-2 border-primary text-primary' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                    <FaLock /> Access Control (ABAC)
                </button>
            </div>
            
            <div className="p-6 space-y-6 overflow-y-auto flex-grow">
                
                {/* --- TAB 1: ROLES & TERRITORY --- */}
                {activeModalTab === 'roles' && (
                    <div className="space-y-6">
                        {/* Role Checkboxes */}
                        <div className="space-y-2">
                            <p className="text-xs font-bold text-gray-500 uppercase">System Roles</p>
                            {SYSTEM_ROLES.map(role => {
                                let label = role.replace('_', ' ');
                                if (role === 'dealer') label = 'Car Dealer';
                                return (
                                    <label key={role} className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                                        <input 
                                            type="checkbox" 
                                            checked={roleSelection.includes(role)}
                                            onChange={() => toggleRole(role)}
                                            className="h-5 w-5 text-primary rounded"
                                        />
                                        <span className="ml-3 font-medium text-gray-700 capitalize">{label}</span>
                                    </label>
                                );
                            })}
                        </div>

                        {/* CASCADING TERRITORY ASSIGNMENT */}
                        {roleSelection.includes('territory_coordinator') && (
                            <div className="bg-blue-50 p-4 rounded border border-blue-200 animate-fadeIn">
                                <h4 className="text-sm font-bold text-blue-800 mb-4 flex items-center gap-2">
                                    <FaMapMarkedAlt /> Assign Territory
                                </h4>
                                
                                {/* Level Selector */}
                                <div className="flex flex-col sm:flex-row gap-4 mb-4 border-b border-blue-200 pb-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input 
                                            type="radio" 
                                            name="level" 
                                            checked={coordinatorLevel === 'state'} 
                                            onChange={() => setCoordinatorLevel('state')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm font-bold text-gray-700">State Coordinator</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input 
                                            type="radio" 
                                            name="level" 
                                            checked={coordinatorLevel === 'lga'} 
                                            onChange={() => setCoordinatorLevel('lga')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm font-bold text-gray-700">LGA Coordinator</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input 
                                            type="radio" 
                                            name="level" 
                                            checked={coordinatorLevel === 'zone'} 
                                            onChange={() => setCoordinatorLevel('zone')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm font-bold text-gray-700">Zonal Coordinator</span>
                                    </label>
                                </div>

                                {/* CASCADING COLUMNS */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-auto md:h-80">
                                    {/* COLUMN 1: STATES */}
                                    <div className="border rounded bg-white flex flex-col h-56 md:h-full">
                                        <div className="p-2 bg-gray-100 border-b font-bold text-xs text-gray-600 uppercase">1. Select States ({selectedStates.length})</div>
                                        <div className="overflow-y-auto p-2 space-y-1 flex-grow">
                                            {Object.keys(lgasByState).sort().map(state => (
                                                <label key={state} className={`flex items-center p-2 rounded cursor-pointer ${selectedStates.includes(state) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                                    <input type="checkbox" checked={selectedStates.includes(state)} onChange={() => toggleState(state)} className="h-4 w-4 text-blue-600 rounded" />
                                                    <span className="ml-2 text-sm text-gray-700">{state}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    {/* COLUMN 2: LGAs */}
                                    <div className={`border rounded bg-white flex flex-col h-56 md:h-full ${coordinatorLevel === 'state' ? 'opacity-50 pointer-events-none bg-gray-100' : ''}`}>
                                        <div className="p-2 bg-gray-100 border-b font-bold text-xs text-gray-600 uppercase">2. Select LGAs ({selectedLgas.length})</div>
                                        <div className="overflow-y-auto p-2 space-y-1 flex-grow">
                                            {selectedStates.length === 0 ? <p className="text-xs text-gray-400 text-center mt-4">Select a State first</p> : selectedStates.map(state => (
                                                <div key={state} className="mb-2"><p className="text-[10px] font-bold text-gray-400 uppercase mb-1 pl-1">{state}</p>{lgasByState[state]?.map(lga => (
                                                    <label key={lga.id} className={`flex items-center p-2 rounded cursor-pointer ${selectedLgas.includes(lga.lga) ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                                                        <input type="checkbox" checked={selectedLgas.includes(lga.lga)} onChange={() => toggleLga(lga.lga, lga.id)} className="h-4 w-4 text-green-600 rounded" />
                                                        <span className="ml-2 text-sm text-gray-700">{lga.lga}</span>
                                                    </label>
                                                ))}</div>
                                            ))}
                                        </div>
                                    </div>
                                    {/* COLUMN 3: ZONES */}
                                    <div className={`border rounded bg-white flex flex-col h-56 md:h-full ${coordinatorLevel !== 'zone' ? 'opacity-50 pointer-events-none bg-gray-100' : ''}`}>
                                        <div className="p-2 bg-gray-100 border-b font-bold text-xs text-gray-600 uppercase">3. Select Zones ({selectedZones.length})</div>
                                        <div className="overflow-y-auto p-2 space-y-1 flex-grow">
                                            {selectedLgas.length === 0 ? <p className="text-xs text-gray-400 text-center mt-4">Select an LGA first</p> : selectedLgas.map(lgaName => {
                                                const lgaObj = lgas.find(l => l.lga === lgaName);
                                                const lgaId = lgaObj?.id;
                                                const zonesForLga = zonesByLgaId[lgaId] || [];
                                                return (
                                                    <div key={lgaName} className="mb-2"><p className="text-[10px] font-bold text-gray-400 uppercase mb-1 pl-1">{lgaName}</p>{zonesForLga.length === 0 ? <p className="text-xs text-gray-400 pl-2 italic">No zones defined</p> : zonesForLga.map(zone => (
                                                        <label key={zone.id} className={`flex items-center p-2 rounded cursor-pointer ${selectedZones.includes(zone.zone_name) ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                                                            <input type="checkbox" checked={selectedZones.includes(zone.zone_name)} onChange={() => toggleZone(zone.zone_name)} className="h-4 w-4 text-purple-600 rounded" />
                                                            <span className="ml-2 text-sm text-gray-700">{zone.zone_name}</span>
                                                        </label>
                                                    ))}</div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* --- TAB 2: ACCESS CONTROL (ABAC) --- */}
                {activeModalTab === 'permissions' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                        
                        {/* ALLOWED TABS */}
                        <div className="border rounded-lg flex flex-col h-full bg-white">
                            <div className="p-3 bg-green-50 border-b border-green-100 flex justify-between items-center">
                                <h4 className="font-bold text-green-800 text-sm flex items-center gap-2"><FaListAlt /> Allowed Tabs</h4>
                                <button 
                                    onClick={() => setAllowedTabs(allowedTabs.length === ALL_TABS.length ? [] : ALL_TABS.map(t => t.id))}
                                    className="text-xs text-green-600 hover:underline"
                                >
                                    {allowedTabs.length === ALL_TABS.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="p-2 overflow-y-auto flex-grow space-y-1 max-h-[400px]">
                                {ALL_TABS.map(tab => (
                                    <label key={tab.id} className={`flex items-center p-2 rounded cursor-pointer ${allowedTabs.includes(tab.id) ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={allowedTabs.includes(tab.id)}
                                            onChange={() => toggleTabPermission(tab.id)}
                                            className="h-4 w-4 text-green-600 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">{tab.label}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="p-2 bg-gray-50 text-[10px] text-gray-500 border-t">
                                * If no tabs are checked, default role-based tabs will be shown.
                            </div>
                        </div>

                        {/* RESTRICTED ACTIONS */}
                        <div className="border rounded-lg flex flex-col h-full bg-white">
                            <div className="p-3 bg-red-50 border-b border-red-100 flex justify-between items-center">
                                <h4 className="font-bold text-red-800 text-sm flex items-center gap-2"><FaBan /> Restricted Actions</h4>
                                <button 
                                    onClick={() => setRestrictedActions(restrictedActions.length === USER_ACTIONS.length ? [] : USER_ACTIONS.map(a => a.id))}
                                    className="text-xs text-red-600 hover:underline"
                                >
                                    {restrictedActions.length === USER_ACTIONS.length ? 'Unblock All' : 'Block All'}
                                </button>
                            </div>
                            <div className="p-2 overflow-y-auto flex-grow space-y-1 max-h-[400px]">
                                {USER_ACTIONS.map(action => (
                                    <label key={action.id} className={`flex items-center p-2 rounded cursor-pointer ${restrictedActions.includes(action.id) ? 'bg-red-50 border border-red-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={restrictedActions.includes(action.id)}
                                            onChange={() => toggleActionRestriction(action.id)}
                                            className="h-4 w-4 text-red-600 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">{action.label}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="p-2 bg-gray-50 text-[10px] text-gray-500 border-t">
                                * Checked actions will be hidden/disabled for this user.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setSelectedUser(null)} className="px-4 py-2 text-gray-600">Cancel</button>
              <button onClick={handleSaveRoles} disabled={saving} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center gap-2">
                {saving ? 'Saving...' : <><FaCheck /> Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* --- REGISTER MODAL (Existing) --- */}
      {isRegisterModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-fadeInUp">
                  <div className="flex justify-between items-center p-6 border-b border-gray-200">
                      <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                          <FaUserPlus className="text-green-600" /> Register New Customer
                      </h3>
                      <button onClick={() => setIsRegisterModalOpen(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                  </div>
                  <form onSubmit={handleRegisterCustomer} className="p-6 space-y-4">
                      <input type="text" value={registerData.full_name} onChange={e => setRegisterData({...registerData, full_name: e.target.value})} required className="w-full p-2 border rounded" placeholder="Full Name" />
                      <input type="email" value={registerData.email} onChange={e => setRegisterData({...registerData, email: e.target.value})} required className="w-full p-2 border rounded" placeholder="Email" />
                      <input type="password" value={registerData.password} onChange={e => setRegisterData({...registerData, password: e.target.value})} required minLength={6} className="w-full p-2 border rounded" placeholder="Password" />
                      <div className="flex justify-center gap-3 pt-4 border-t">
                          <button type="button" onClick={() => setIsRegisterModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button type="submit" disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2">
                              {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />} Create Account
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

    </div>
  );
}

export default AdminUsers;